<template>
  <card>
    <h4 slot="header" class="card-title">{{ $t("message.employees") }}</h4>
    <b-row>
      <b-col>
        <b-form class="d-flex mb-2" inline @submit.prevent>
          <b-form-select
            v-model="perPage"
            aria-controls="employees-table"
            :options="perPageOptions"
          >
          </b-form-select>
          <fg-input
            id="employees__searchQuery"
            class="ml-2"
            v-model="searchQuery"
            placeholder="Search..."
            aria-label="Search"
            aria-controls="employees-table"
          ></fg-input>
          <router-link
            class="ml-auto mt-0 btn btn-primary"
            to="/management/employees/add"
            v-if="employees.length"
          >
            {{ $t("message.add") }}
          </router-link>
        </b-form>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-table
          id="employees__table"
          :items="employees"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :filter="searchQuery"
          :per-page="perPage"
          :current-page="currentPage"
          :busy="loading"
          :show-empty="!loading && !employees.length"
          stacked="md"
          striped
          @filtered="
            filteredItems => (searchItemsLength = filteredItems.length)
          "
        >
          <template v-slot:table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>
          <template v-slot:empty>
            <div v-if="error" class="text-center text-danger my-2">
              <p>{{ $t("message.somethingWentWrong") }}</p>
              <p>{{ error }}</p>
            </div>
            <div v-else class="text-center text-primary my-2">
              <p>{{ $t("message.noEmployees") }}</p>
              <router-link
                to="/management/employees/add"
                class="btn btn-primary btn-round"
              >
                {{ $t("message.addAnEmployee") }}
              </router-link>
            </div>
          </template>
          <template v-slot:cell(name)="data">
            <router-link
              tag="a"
              class="ml-2"
              :to="`/management/employees/edit/${data.item._id}`"
            >
              <avatar
                :src="data.item.image"
                :alt="$t('message.avatarFor', { name: data.item.name })"
              />
              <span class="ml-2">{{ data.item.name }}</span>
            </router-link>
          </template>
          <template v-slot:cell(actions)="data">
            <drop-down
              class="row-actions"
              tag="button"
              position="right"
              icon="fa fa-ellipsis-v"
              hideArrow
            >
              <button
                class="dropdown-item"
                @click.prevent="confirmDeleteEmployee(data.item)"
              >
                {{ $t("message.delete") }}
              </button>
            </drop-down>
          </template>
        </b-table>
        <b-pagination
          id="employees__pagination"
          v-model="currentPage"
          :total-rows="employees.length"
          :per-page="perPage"
          align="right"
          aria-controls="employees-table"
          first-number
          last-number
        ></b-pagination>
      </b-col>
    </b-row>
  </card>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { errorMessage } from "@/helpers/bookingSearch";
import {
  employeeRosterCleanupLines,
  formatEmployeeBookingWhen
} from "@/helpers/employeeRosterCleanup";
import { timestampToDateAndTime } from "@/helpers/time";
import { Avatar } from "@/components";

export default {
  components: {
    Avatar
  },
  data() {
    return {
      loading: true,
      error: null,
      fields: [
        {
          key: "name",
          label: this.$t("message.employee"),
          sortable: true
        },
        {
          key: "lastModified",
          label: this.$t("message.lastModified"),
          sortable: true,
          formatter: timestampToDateAndTime
        },
        {
          key: "actions",
          label: "",
          sortable: false
        }
      ],
      searchQuery: null,
      searchItemsLength: 0,
      sortBy: "name",
      sortDesc: false,
      perPage: 5,
      currentPage: 1,
      perPageOptions: [5, 10, 25, 50]
    };
  },
  methods: {
    ...mapActions({
      fetchEmployees: "employees/fetchEmployees",
      previewEmployeeDelete: "employees/previewEmployeeDelete",
      deleteEmployee: "employees/deleteEmployee"
    }),
    deletePreviewHtml(summary) {
      const lines = employeeRosterCleanupLines(summary && summary.roster ? summary.roster : summary);
      const blocks = [
        `<p>${this.$t("message.employeeDeleteLead")}</p>`
      ];
      if (lines.length) {
        blocks.push(`<p>${this.$t("message.employeeDeleteRosterIntro")}</p>`);
        blocks.push(
          `<p>${lines.map(line => this.escapeHtml(line)).join("<br>")}</p>`
        );
      } else {
        blocks.push(`<p>${this.$t("message.employeeDeleteNoRoster")}</p>`);
      }
      blocks.push(`<p>${this.$t("message.employeeDeleteBookingsStay")}</p>`);
      return blocks.join("");
    },
    escapeHtml(value) {
      return String(value || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    async confirmDeleteEmployee(employee) {
      try {
        const preview = await this.previewEmployeeDelete(employee);
        const name = employee.name || "this person";
        if (preview.bookings && preview.bookings.blocked) {
          const when =
            formatEmployeeBookingWhen(preview.bookings.lastStart) ||
            "upcoming";
          await this.$swal({
            icon: "info",
            title: this.$t("message.employeeDeleteBlockedTitle", { name }),
            text:
              preview.bookings.count === 1
                ? this.$t("message.employeeDeleteBlockedOne", { when })
                : this.$t("message.employeeDeleteBlockedMany", {
                    count: preview.bookings.count,
                    when
                  }),
            confirmButtonText: this.$t("message.ok")
          });
          return;
        }
        const result = await this.$swal({
          icon: "warning",
          title: this.$t("message.employeeDeleteTitle", { name }),
          html: this.deletePreviewHtml(preview),
          showCancelButton: true,
          focusCancel: true,
          confirmButtonText: this.$t("message.employeeDeleteConfirm"),
          cancelButtonText: this.$t("message.cancel")
        });

        if (!result.value) return;

        await this.deleteEmployee(employee);
        this.$notify({
          type: "success",
          title: "Success",
          text: this.$t("message.employeeDeleted")
        });
      } catch (e) {
        this.$notify({
          type: "danger",
          title: this.$t("message.error"),
          text: errorMessage(e, "Could not delete employee.")
        });
      }
    }
  },
  computed: mapGetters({
    employees: "employees/employeesList"
  }),
  async created() {
    try {
      await this.fetchEmployees();
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
    }
  }
};
</script>
